import { useState, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  FormControl,
  InputLabel,
  TextField,
  FormLabel,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import Header from "../../components/Header";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  getShiftTypes,
  getTimesheetDetails,
  getUserProjects,
  getUserSettings,
  getUserTakByProject,
  submitTimesheet,
} from "../../services/timesheet";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import Radio from "@mui/joy/Radio";
import RadioGroupJoy from "@mui/joy/RadioGroup";
import Button from "../../components/ui/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/material/styles";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import { Button as MuiButton } from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import { createTimesheetSchema } from "../../schemas/applyTimesheetSchema";
import {
  formatDateToHHMM,
  formatDateToYYYYMMDD,
} from "../../Helper/functionHelper";
import useCustomFields from "../../hooks/ useCustomFields";
import CustomFieldRenderer from "../../components/common/CustomFieldRenderer";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const parseTimeStringToDate = (timeStr) => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const dayOptions = [
  { label: "Week Day", value: "WD" },
  { label: "Week Off", value: "WO" },
  { label: "Holiday", value: "HD" },
];

const TimesheetForm = () => {

  const { customFields } = useCustomFields("timesheet");
  const [shiftTypes, setShiftTypes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [userSettings, setUserSettings] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [coordinates, setCoordinates] = useState({ lat: null, lng: null });
  const [timesheetStatus, setTimesheetStatus] = useState(null);
  const [approveRejectReason, setApproveRejectReason] = useState(null);
  const [actionerName, setActionerName] = useState(null);

  const navigate = useNavigate();
  const { timesheetId } = useParams();

  const schema = useMemo(
    () => createTimesheetSchema(customFields),
    [customFields]
  );

  const {
    handleSubmit,
    formState: { isSubmitting, errors },
    control,
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      timesheet_date: new Date(),
      timesheet_day: "WD",
      shift_type: null,
      project: null,
      task: null,
      entry_type: "1",
      timesheet_hours: parseTimeStringToDate("06:00"),
      start_time: parseTimeStringToDate("06:00"),
      end_time: parseTimeStringToDate("06:00"),
      comp_off: false,
      description: "",
      custom_fields: {}
    },
  });

  const entryType = watch("entry_type");
  const dayType = watch("timesheet_day");

  // Function to get current location with retries
  const getCurrentLocation = (retries = 3, timeout = 30000) => {
    return new Promise((resolve, reject) => {
      const attemptLocation = (attemptsLeft) => {
        if (!("geolocation" in navigator)) {
          reject(new Error("Geolocation is not supported by your browser."));
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            const coords = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            setCoordinates(coords);
            resolve(coords);
          },
          (error) => {
            console.error(
              `Location attempt failed (${attemptsLeft} left):`,
              error
            );

            if (attemptsLeft > 1) {
              // Retry after a short delay
              setTimeout(() => attemptLocation(attemptsLeft - 1), 1000);
            } else {
              // All retries exhausted
              let errorMessage = "Unable to retrieve your location. ";
              switch (error.code) {
                case error.PERMISSION_DENIED:
                  errorMessage +=
                    "Please allow location access in your browser settings.";
                  break;
                case error.POSITION_UNAVAILABLE:
                  errorMessage += "Location information is unavailable.";
                  break;
                case error.TIMEOUT:
                  errorMessage += "Location request timed out.";
                  break;
                default:
                  errorMessage += "An unknown error occurred.";
              }
              reject(new Error(errorMessage));
            }
          },
          {
            enableHighAccuracy: true,
            timeout: timeout,
            maximumAge: 0,
          }
        );
      };

      attemptLocation(retries);
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [shiftRes, projectsRes, userSettingsRes] = await Promise.all([
          getShiftTypes(),
          getUserProjects(),
          getUserSettings(),
        ]);

        if (shiftRes.status === 200) {
          setShiftTypes(shiftRes.data.data.shiftTypeData);
        }
        if (projectsRes.status === 200) {
          setProjects(projectsRes.data.data.userProjects);
        }
        if (userSettingsRes.status === 200) {
          setUserSettings(userSettingsRes.data.data);

          if (
            userSettingsRes.data?.data?.settings?.timesheetLocationRequired ===
            "1" &&
            !timesheetId
          ) {
            getCurrentLocation().catch((error) => {
              console.error("Initial location fetch failed:", error);
            });
          }
        }

        if (
          !timesheetId &&
          shiftRes.status === 200 &&
          userSettingsRes.status === 200 &&
          userSettingsRes.data?.data?.settings?.userShift
        ) {
          const userShiftId = userSettingsRes.data.data.settings.userShift;
          const matchingShift = shiftRes.data.data.shiftTypeData.find(
            (shift) => shift.shiftTypeId === userShiftId
          );
          if (matchingShift) {
            setValue("shift_type", userShiftId);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFormSubmit = async (data) => {
    if (
      userSettings?.settings?.timesheetLocationRequired === "1" &&
      !timesheetId
    ) {
      if (!coordinates.lat || !coordinates.lng) {
        try {
          const coords = await getCurrentLocation(3, 15000);
          data.latitude = coords.lat;
          data.longitude = coords.lng;
        } catch (error) {
          toast.error(
            error.message ||
            "Location access is required to submit the timesheet."
          );
          return;
        }
      } else {
        data.latitude = coordinates.lat;
        data.longitude = coordinates.lng;
      }
    }

    // Format date/time fields
    const formattedData = {};
    Object.entries(data).forEach(([key, value]) => {
      if (value instanceof Date) {
        if (
          key === "timesheet_hours" ||
          key === "start_time" ||
          key === "end_time"
        ) {
          formattedData[key] = formatDateToHHMM(value);
        } else if (key === "timesheet_date") {
          formattedData[key] = formatDateToYYYYMMDD(value);
        }
      } else {
        if (key === "custom_fields") {
          formattedData[key] = JSON.stringify(value);
        }
        else {
          formattedData[key] = value;
        }
      }
    });

    if (timesheetId) {
      formattedData.timesheet_id = timesheetId;
    }

    const formData = new FormData();
    Object.entries(formattedData).forEach(([key, value]) => {
      formData.append(key, value);
    });

    const existingDocNames = [];
    uploadedFiles.forEach((file) => {
      if (file.isExisting) {
        existingDocNames.push(file.path);
      } else {
        formData.append("documents", file);
      }
    });

    formData.append("existingDocuments", existingDocNames.join(","));

    try {
      const res = await submitTimesheet(formData);
      if (res.status === 200 && res.data?.status) {
        toast.success(res.data?.message || "Timesheet submitted!");
        navigate("/timesheet");
      } else if (res.data?.message) {
        toast.error(res.data.message);
      } else {
        toast.error("Failed to submit timesheet. Please try again.");
      }
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Error submitting timesheet. Please try again.");
      }
      console.error("Error submitting timesheet:", error);
    }
  };

  const handleFileUpload = (e) => {
    const allowedTypes = [
      ".doc",
      ".docx",
      ".pdf",
      ".xls",
      ".xlsx",
      ".jpg",
      ".jpeg",
      ".png",
    ];
    const maxSize = 4 * 1024 * 1024;
    const maxFiles = 5;

    const files = Array.from(e.target.files);
    const totalFilesCount = uploadedFiles.length + files.length;
    if (totalFilesCount > maxFiles) {
      toast.error(`You can upload a maximum of ${maxFiles} files.`);
      e.target.value = null;
      return;
    }

    const validFiles = [];
    const invalidTypeFiles = [];
    const oversizedFiles = [];

    files.forEach((file) => {
      const ext = "." + file.name.split(".").pop().toLowerCase();
      if (!allowedTypes.includes(ext)) {
        invalidTypeFiles.push(file.name);
      } else if (file.size > maxSize) {
        oversizedFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    });

    setUploadedFiles((prev) => [...prev, ...validFiles]);

    let message = "";
    if (invalidTypeFiles.length)
      message += `${invalidTypeFiles.length} invalid file(s) rejected.\n`;
    if (oversizedFiles.length)
      message += `${oversizedFiles.length} oversized file(s) rejected.\n`;
    if (message) toast.error(message);

    e.target.value = null;
  };

  const removeDocument = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const fetchTask = async (project_id) => {
    try {
      const res = await getUserTakByProject(project_id);
      const data = res.data.data;
      setTasks(data);
    } catch (error) {
      console.log("Error in fetch task", error);
    }
  }

  useEffect(() => {
    if (dayType === "WD") {
      setValue("comp_off", false);
    }
    // eslint-disable-next-line
  }, [dayType]);

  const pageTitle = timesheetId ? "Edit Timesheet" : "Add Timesheet";
  const submitButtonText = timesheetId ? "Update Timesheet" : "Add Timesheet";

  // for edit
  useEffect(() => {
    if (timesheetId) {
      getTimesheetDetails(timesheetId)
        .then(async (res) => {
          if (res.status === 200) {
            const timesheet = res.data.data;
            setTimesheetStatus(timesheet.timesheetStatus);
            setApproveRejectReason(timesheet.approveRejectReason);
            setActionerName(timesheet.actionerName);
            setValue("timesheet_date", new Date(timesheet.timesheetDate));
            setValue("timesheet_day", timesheet.timesheetDay);
            setValue("shift_type", timesheet.shiftType);
            setValue("project", timesheet.projectId);
            setValue("document", timesheet.document);
            setValue("description", timesheet.description || "");

            await fetchTask(timesheet.projectId);

            if (timesheet.taskId || timesheet.task) {
              setValue("task", timesheet.taskId || timesheet.task);
            }

            const customDefaults = {};
            if (Array.isArray(timesheet.custom_fields)) {
              timesheet.custom_fields.forEach(({ field_key, value }) => {
                customDefaults[field_key] = value;
              });
            }

            setValue("custom_fields", customDefaults);

            if (timesheet.startTime && timesheet.endTime) {
              setValue("entry_type", "2");
              setValue(
                "start_time",
                parseTimeStringToDate(timesheet.startTime)
              );
              setValue("end_time", parseTimeStringToDate(timesheet.endTime));
            } else {
              setValue("entry_type", "1");
              setValue(
                "timesheet_hours",
                parseTimeStringToDate(timesheet.timesheetHours)
              );
            }
            if (
              timesheet.timesheetDay === "WO" ||
              timesheet.timesheetDay === "HD"
            ) {
              if (timesheet.compoff === "1") {
                setValue("comp_off", true);
              }
            }

            if (timesheet.document) {
              const docNames = timesheet.document
                .split(",")
                .filter((d) => d.trim() !== "");

              const parsedDocs = docNames.map((name) => ({
                path: name.trim(),
                isExisting: true,
              }));
              setUploadedFiles(parsedDocs);
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching timesheet details:", error);
          if (error.response?.status === 403) {
            toast.error("You are not authorized to access this timesheet.");
            navigate("/timesheet");
          }
        });
    }
    // eslint-disable-next-line
  }, [timesheetId]);

  // ✅ Once customFields load, inject their defaults into the form
  useEffect(() => {
    if (timesheetId) return;
    if (!customFields.length) return;

    const customDefaults = {};
    customFields.forEach((cf) => {
      switch (cf.field_type) {
        case "checkbox":
          customDefaults[cf.field_key] = false;
          break;
        case "date":
          customDefaults[cf.field_key] = null;
          break;
        case "number":
          customDefaults[cf.field_key] = "";
          break;
        default: // text, textarea, dropdown
          customDefaults[cf.field_key] = "";
      }
    });

    // Only sets custom_fields, leaves all other form values untouched
    setValue("custom_fields", customDefaults);
  }, [customFields]);

  const timesheetMinDate = new Date();
  timesheetMinDate.setDate(timesheetMinDate.getDate() - 14);

  return (
    <>
      <Header title={pageTitle}>
        <span className="add float-end">
          <Link to="/timesheet" className="btn btn-secondary btn-sm">
            <span className="icon">
              <img src="/public/images/back-arrow.svg" alt="back" />
            </span>
            <span className="text">Back</span>
          </Link>
        </span>
      </Header>
      <div className="tab-profile mt-3">
        <div className="content-block">
          <div className="container-fluid">
            <form onSubmit={handleSubmit(onFormSubmit)}>
              <div className="row mb-3">
                <div className="col-lg-3 col-md-6">
                  <Controller
                    name="timesheet_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        label="Date*"
                        format="dd MMM yyyy"
                        minDate={timesheetMinDate}
                        maxDate={new Date()}
                        slotProps={{
                          textField: {
                            variant: "standard",
                            fullWidth: true,
                            error: !!errors.timesheet_date,
                            helperText: errors.timesheet_date?.message,
                            sx: { marginTop: "-3px" },
                          },
                        }}
                        disabled={isSubmitting}
                      />
                    )}
                  />
                </div>

                <div className="col-lg-3 col-md-6">
                  {timesheetId ? (
                    <div className="form-label-group">
                      <label>Day*</label>
                      <div className="mt-1">
                        {dayOptions.find(
                          (o) => o.value === watch("timesheet_day")
                        )?.label || "-"}
                      </div>
                    </div>
                  ) : (
                    <Controller
                      name="timesheet_day"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          options={dayOptions}
                          getOptionLabel={(option) => option.label}
                          value={
                            dayOptions.find((o) => o.value === field.value) ||
                            null
                          }
                          onChange={(_, val) =>
                            field.onChange(val?.value || "")
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Day*"
                              variant="standard"
                              error={!!errors.timesheet_day}
                              helperText={errors.timesheet_day?.message}
                            />
                          )}
                        />
                      )}
                    />
                  )}
                </div>

                <div className="col-lg-3 col-md-6">
                  <Controller
                    name="shift_type"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        options={shiftTypes}
                        getOptionLabel={(option) => option.shiftTypeTitle || ""}
                        value={
                          shiftTypes.find(
                            (o) => o.shiftTypeId === field.value
                          ) || null
                        }
                        onChange={(_, val) =>
                          field.onChange(val ? val.shiftTypeId : "")
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Shift Type*"
                            variant="standard"
                            error={!!errors.shift_type}
                            helperText={errors.shift_type?.message}
                            disabled={isSubmitting}
                          />
                        )}
                      />
                    )}
                  />
                </div>

                <div className="col-lg-3 col-md-6">
                  <Controller
                    name="project"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        options={projects}
                        getOptionLabel={(option) => option.projectName || ""}
                        value={
                          projects.find((p) => p.projectId === field.value) ||
                          null
                        }
                        onChange={async (_, val) => {
                          field.onChange(val ? val.projectId : "");
                          await fetchTask(val?.projectId);
                        }
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Project*"
                            variant="standard"
                            error={!!errors.project}
                            disabled={isSubmitting}
                            helperText={errors.project?.message}
                          />
                        )}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-lg-3 col-md-6">
                  <Controller
                    name="task"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        options={tasks}
                        getOptionLabel={(option) => option.title || ""}
                        value={
                          tasks.find((p) => p.id === field.value) ||
                          null
                        }
                        onChange={(_, val) =>
                          field.onChange(val ? val.id : "")
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Task"
                            variant="standard"
                            disabled={isSubmitting}
                            
                          />
                        )}
                      />
                    )}
                  />
                </div>
                <div className="col-lg-3 col-md-6">
                  <FormControl fullWidth>
                    <FormLabel>Working Hour(s)</FormLabel>
                    <Controller
                      name="entry_type"
                      control={control}
                      render={({ field }) => (
                        <RadioGroupJoy {...field} orientation="horizontal">
                          <Radio
                            sx={{
                              fontSize: "var(--bs-body-font-size) !important",
                            }}
                            value="1"
                            label="Hours Spent"
                          />
                          <Radio
                            sx={{
                              fontSize: "var(--bs-body-font-size) !important",
                            }}
                            value="2"
                            label="Time Duration"
                          />
                        </RadioGroupJoy>
                      )}
                    />
                    {errors.entry_type && (
                      <p className="text-danger">{errors.entry_type.message}</p>
                    )}
                  </FormControl>
                </div>

                {entryType === "1" && (
                  <div className="col-lg-3 col-md-6">
                    <Controller
                      name="timesheet_hours"
                      control={control}
                      render={({ field }) => (
                        <TimePicker
                          {...field}
                          label="Hours (HH:MM)*"
                          ampm={false}
                          format="HH:mm"
                          disabled={isSubmitting}
                          slotProps={{
                            textField: {
                              variant: "standard",
                              fullWidth: true,
                              error: !!errors.timesheet_hours,
                              helperText: errors.timesheet_hours?.message,
                            },
                            actionBar: {
                              actions: [],
                            },
                          }}
                        />
                      )}
                    />
                  </div>
                )}

                {entryType === "2" && (
                  <>
                    <div className="col-lg-3 col-md-6">
                      <Controller
                        name="start_time"
                        control={control}
                        render={({ field }) => (
                          <TimePicker
                            {...field}
                            disabled={isSubmitting}
                            label="Start Time*"
                            ampm={false}
                            format="HH:mm"
                            slotProps={{
                              textField: {
                                variant: "standard",
                                fullWidth: true,
                                error: !!errors.start_time,
                                helperText: errors.start_time?.message,
                              },
                              actionBar: {
                                actions: [],
                              },
                            }}
                          />
                        )}
                      />
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <Controller
                        name="end_time"
                        control={control}
                        render={({ field }) => (
                          <TimePicker
                            {...field}
                            disabled={isSubmitting}
                            label="End Time*"
                            ampm={false}
                            format="HH:mm"
                            slotProps={{
                              textField: {
                                variant: "standard",
                                fullWidth: true,
                                error: !!errors.end_time,
                                helperText: errors.end_time?.message,
                              },
                              actionBar: {
                                actions: [],
                              },
                            }}
                          />
                        )}
                      />
                    </div>
                  </>
                )}

                {(dayType === "WO" || dayType === "HD") &&
                  userSettings?.settings?.canApplyCompoff && (
                    <div className="col-lg-3 col-md-6">
                      <Controller
                        name="comp_off"
                        control={control}
                        render={({ field }) => (
                          <FormGroup sx={{ mt: 2 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  disabled={isSubmitting}
                                  {...field}
                                  checked={field.value}
                                  color="primary"
                                />
                              }
                              label="Apply Comp Off"
                            />
                          </FormGroup>
                        )}
                      />
                    </div>
                  )}
              </div>

              {customFields.length > 0 && (
                <div className="row mb-3">
                  {customFields.map((cf) => (
                    <div key={cf.id} className="col-lg-3 col-md-6 mb-3">
                      <CustomFieldRenderer
                        field={cf}
                        control={control}
                        errors={errors}
                        disabled={isSubmitting}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="row mb-3">
                <div className="col-12">
                  <InputLabel>
                    Upload Document (PDF, JPG, PNG, DOCX, XLSX, max 4 MB)
                  </InputLabel>
                  <MuiButton
                    component="label"
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    disabled={isSubmitting}
                  >
                    Upload files
                    <VisuallyHiddenInput
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      accept=".doc,.docx,.pdf,.xls,.xlsx,.jpg,.jpeg,.png"
                    />
                  </MuiButton>
                  <div className="mt-2">
                    <div className="mt-2">
                      {uploadedFiles.map((file, idx) => (
                        <div
                          key={idx}
                          className="d-flex justify-content-between align-items-center border p-1 rounded mb-1"
                        >
                          {file.isExisting ? (
                            <span
                            // href={file.path}
                            // target="_blank"
                            // rel="noopener noreferrer"
                            >
                              {`Document ${idx + 1}`}
                            </span>
                          ) : (
                            <span>{file.name}</span>
                          )}
                          <CloseIcon
                            fontSize="small"
                            className="text-danger cursor-pointer"
                            onClick={() => removeDocument(idx)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-12">
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description*"
                        variant="standard"
                        multiline
                        rows={5}
                        fullWidth
                        disabled={isSubmitting}
                        error={!!errors.description}
                        helperText={errors.description?.message}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-12 text-end">
                  <Button
                    variant="success"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {submitButtonText}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default TimesheetForm;
